import { DrawerSheet, DrawerSheetTitle } from '../drawer-sheet';
import { BlockLoading } from '../fetch-status/block-loading';
import { TextTooltip } from '../text-tooltip';
import { Button } from '../ui/button';
import { UploadedImage } from '../uploaded-image';
import { InteractCloseIcon } from '@/client/assets';
import { TradePanel } from '@/client/components/trade';
import { useTradeResumeStore } from '@/client/store/trade-resume-store';
import { useTradeParamsStore } from '@/client/store/trade-store';
import { cn } from '@/client/utils';
import { api } from '@/client/utils/api';
import { pickI18n } from '@/client/utils/i18n';
import { useMarketDetail } from '@/libs/react-sdk/api/use-market-detail';
import { useTranslation } from 'react-i18next';

type TradeSidebarProps = {
  className?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
};

export const TradeSidebar = ({ className, open, setOpen }: TradeSidebarProps) => {
  const { t } = useTranslation();
  const resumeNotice = useTradeResumeStore(state => state.resumeNotice);
  const marketAddress = useTradeParamsStore(state => state.marketAddress);
  const { data: market, isLoading } = useMarketDetail(api, { marketAddress });
  if (!open) return null;
  if (!market || isLoading) return <BlockLoading />;
  return (
    <DrawerSheet
      open={open}
      setOpen={setOpen}
      transactionMarket={marketAddress}
      className={cn(
        'md:p-3 md:border-l-.5 md:border-foreground/10 md:bg-[#F4F5F6]/80 md:dark:bg-background md:w-[407px] md:backdrop-blur-[25px] md:shadow-[-2px_0px_4px_0px] shadow-card/10',
        className
      )}
    >
      <DrawerSheetTitle className="space-x-2 flex items-center mb-3">
        <UploadedImage fileKey={market.marketPicUrl} className="size-5 rounded-1.5" />
        <TextTooltip tooltip={pickI18n(market.nameI18n, market.marketName)} className="line-clamp-3 text-left flex-1">
          {pickI18n(market.nameI18n, market.marketName)}
        </TextTooltip>
        <Button
          size="icon"
          variant="ghost"
          className="hidden md:inline-flex transition-all duration-200 hover:scale-90 focus:scale-80"
          onClick={() => setOpen(false)}
        >
          <InteractCloseIcon className="!size-6 fill-foreground/40" />
        </Button>
      </DrawerSheetTitle>
      {resumeNotice && (
        <p role="status" className="mb-3 rounded-3 bg-muted p-3 text-sm text-muted-foreground">
          {t('trade-draft-restored')}
        </p>
      )}
      <TradePanel tradeClassName="md:shadow-none md:border-none" />
    </DrawerSheet>
  );
};
